export const DOCUMENT_SECTION_ORDER = [
  'Contributor Details',
  'Contact Details',
  'Additional KYC Details',
  'FATCA',
  'Nominee Details',
  "Investor's Bank Details",
  'Authorised Signatory',
  'Declaration of UBO',
  'Promoters / Persons holding control',
  'Address Proof',
  'POA Documents',
  'Additional Documents',
  'KRA Documents',
] as const;

export type DocumentSectionHeading = (typeof DOCUMENT_SECTION_ORDER)[number];

export function getDocumentSectionHeading(
  documentType = '',
  documentName = ''
): DocumentSectionHeading {
  const type = documentType.toLowerCase();
  const name = documentName.toLowerCase();
  const text = `${type} ${name}`;

  if (type === 'kra' || /kra document/.test(name)) {
    return 'KRA Documents';
  }

  if (
    /authoris|authoriz/.test(text) ||
    /signator/.test(text) ||
    (/specimen/.test(text) && /signator/.test(text))
  ) {
    return 'Authorised Signatory';
  }

  if (/ubo|beneficial/.test(text)) {
    return 'Declaration of UBO';
  }

  if (/promoter|holding control/.test(text)) {
    return 'Promoters / Persons holding control';
  }

  if (
    [
      'investorsignature',
      'signature_of_investor',
      'investorphotograph',
      'photograph_of_investor',
      'identityproof',
      'relationship',
      'distributorsignature',
    ].includes(type)
  ) {
    return 'Contributor Details';
  }

  if (
    ['correspondenceaddressproof', 'overseaseaddressproof', 'passportcopy', 'piocard'].includes(type)
  ) {
    return 'Contact Details';
  }

  if (/address/.test(text)) {
    return 'Address Proof';
  }

  if (['demat_information', 'sourceoffund', 'declarationfrominvestors'].includes(type)) {
    return 'Additional KYC Details';
  }

  if (
    ['fatca', 'form_10ff_trc', 'trc', 'permanentestablishment'].includes(type) ||
    /fatca|crs/.test(text)
  ) {
    return 'FATCA';
  }

  if (type === 'bankaccountproof' || /bank/.test(text)) {
    return "Investor's Bank Details";
  }

  if (/nominee|guardian|nomination/.test(text)) {
    return 'Nominee Details';
  }

  if (
    /poa/.test(text) ||
    [
      'poanotarized',
      'photo_of_poa',
      'id_of_poa',
      'address_of_poa',
      'authority_letter',
      'pan_of_authorised',
      'address_of_authorised',
      'investorsconsent',
    ].includes(type)
  ) {
    return 'POA Documents';
  }

  return 'Additional Documents';
}

export function groupDocumentsBySection<T extends { documentType: string; documentName?: string }>(
  documents: T[]
): { heading: DocumentSectionHeading; items: { document: T; doc_index: number }[] }[] {
  const buckets = new Map<DocumentSectionHeading, { document: T; doc_index: number }[]>();

  documents.forEach((document, doc_index) => {
    const heading = getDocumentSectionHeading(document.documentType, document.documentName);
    const list = buckets.get(heading) || [];
    list.push({ document, doc_index });
    buckets.set(heading, list);
  });

  return DOCUMENT_SECTION_ORDER.filter((heading) => buckets.has(heading)).map((heading) => ({
    heading,
    items: buckets.get(heading) || [],
  }));
}
